import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Cpu, Brain, MessageSquare, Database, Cloud, Zap, Code, Layers } from 'lucide-react';
import './TechStack.css';

const TechStack = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });



  const technologies = [
    {
      id: 'ai-integration',
      name: 'AI Integration',
      category: 'AI Foundation',
      icon: Brain,
      color: '#00d9ff',
      description: 'Seamless integration of artificial intelligence into business processes',
      capabilities: ['Natural Language Processing', 'Machine Learning', 'Intelligent Automation']
    },
    {
      id: 'n8n-automation',
      name: 'n8n Automation',
      category: 'Workflow Engine',
      icon: Zap,
      color: '#ea4b71',
      description: 'Visual workflow automation connecting services seamlessly',
      capabilities: ['Visual Workflow Design', 'API Integration', 'Process Automation']
    },
    {
      id: 'whatsapp-business',
      name: 'WhatsApp Business',
      category: 'Communication',
      icon: MessageSquare,
      color: '#25d366',
      description: 'Direct customer engagement through trusted messaging platform',
      capabilities: ['Real-time Messaging', 'Media Sharing', 'Business Features']
    },
    {
      id: 'custom-ai-models',
      name: 'Custom AI Models',
      category: 'Machine Learning',
      icon: Cpu,
      color: '#ff6b6b',
      description: 'Tailored AI solutions built for specific business requirements',
      capabilities: ['Custom Training', 'Domain Expertise', 'Predictive Analytics']
    },
    {
      id: 'cloud-infrastructure',
      name: 'Cloud Infrastructure',
      category: 'Backend Systems',
      icon: Cloud,
      color: '#4ecdc4',
      description: 'Scalable and secure cloud architecture for enterprise applications',
      capabilities: ['Auto Scaling', 'High Availability', 'Security']
    },
    {
      id: 'data-management',
      name: 'Data Management',
      category: 'Data Operations',
      icon: Database,
      color: '#a8e6cf',
      description: 'Comprehensive data storage, organization, and governance solutions',
      capabilities: ['Data Warehousing', 'Data Governance', 'Storage Optimization']
    },
    {
      id: 'data-engineering',
      name: 'Data Engineering',
      category: 'Data Pipeline',
      icon: Code,
      color: '#ffd93d',
      description: 'Building robust data pipelines and processing systems',
      capabilities: ['ETL Operations', 'Stream Processing', 'Data Transformation']
    },
    {
      id: 'microservices',
      name: 'Microservices',
      category: 'Architecture',
      icon: Layers,
      color: '#ff9ff3',
      description: 'Modular architecture ensuring scalability and maintainability',
      capabilities: ['Service Isolation', 'Independent Scaling', 'Fault Tolerance']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.8
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="tech-stack-clean" ref={ref} id="tech-stack">
      <div className="tech-container-clean">
        {/* Header */}
        <motion.div
          className="tech-header-clean"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 1 }}
        >
          <div className="tech-meta">
            <span className="tech-label">TECHNOLOGY STACK</span>
            <span className="tech-version">V2024.1</span>
          </div>
          <h2 className="tech-title-clean">
            <span className="title-row">POWERED BY</span>
            <span className="title-row gradient-text">INNOVATION</span>
          </h2>
          <p className="tech-description-clean">
            A cutting-edge ecosystem of technologies working in perfect harmony
          </p>
        </motion.div>

        {/* Tech Grid */}
        <motion.div
          className="tech-grid-clean"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {technologies.map((tech, index) => (
            <motion.div
              key={tech.id}
              className="tech-card-clean"
              variants={cardVariants}

              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <div className="card-header-clean">
                <div 
                  className="tech-icon-clean"
                  style={{ 
                    backgroundColor: tech.color + '20', 
                    borderColor: tech.color 
                  }}
                >
                  {React.createElement(tech.icon, { 
                    size: 24, 
                    color: tech.color 
                  })}
                </div>
                <div className="tech-info-clean">
                  <h3 className="tech-name-clean">{tech.name}</h3>
                  <span className="tech-category-clean">{tech.category}</span>
                </div>
              </div>

              <p className="tech-description-clean">{tech.description}</p>

              <div className="tech-capabilities">
                {tech.capabilities.map((capability, i) => (
                  <span key={i} className="capability-tag-clean">
                    {capability}
                  </span>
                ))}
              </div>

              <div 
                className="card-accent-clean" 
                style={{ backgroundColor: tech.color }}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Tech Stats */}
        <motion.div
          className="tech-stats-clean"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <div className="stat-item-clean">
            <span className="stat-number">99.9%</span>
            <span className="stat-label">Uptime</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item-clean">
            <span className="stat-number">&lt; 100ms</span>
            <span className="stat-label">Response Time</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item-clean">
            <span className="stat-number">24/7</span>
            <span className="stat-label">Monitoring</span>
          </div>
          <div className="stat-divider"></div>
          <div className="stat-item-clean">
            <span className="stat-number">Global</span>
            <span className="stat-label">Scale</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TechStack; 